/**
 * Service for handling report-related API calls and data management
 */
class ReportService {
  constructor(api) {
    this.api = api;
  }

  /**
   * Get report type options
   */
  async getReportTypeOptions(language = 'en') {
    const { data } = await this.api.reportOptions.getReportOptions(language);
    
    return data.data.map(option => {
      const options = {
        ReportType: option.ReportType,
        ReportOptions: option.ReportOptions.map(ReportOption => {
          return {
            id: ReportOption.Option_PK,
            name: ReportOption.OptionName,
          };
        }),
      };

      if (option.CoverholderOptions) {
        options.CoverholderOptions = option.CoverholderOptions.map(CoverholderOption => {
          return {
            id: CoverholderOption.Company_PK,
            name: CoverholderOption.CompanyName,
          };
        });
      }

      if (option.BinderOptions) {
        options.BinderOptions = option.BinderOptions.map(BinderOption => {
          return {
            id: BinderOption.Binder_PK,
            name: BinderOption.BinderName,
          };
        });
      }
      
      return options;
    });
  }

  /**
   * Fetch dynamic binder options for a specific report type and coverholder
   */
  async fetchBinderOptions(reportType, coverholderId) {
    try {
      const { data } = await this.api.binderOptions.getBinderOptions(reportType, coverholderId);
      
      return data.data.map(binderOption => ({
        id: binderOption.Binder_PK,
        name: binderOption.BinderName,
      }));
    } catch (error) {
      console.error('Error fetching binder options:', error);
      return [];
    }
  }

  /**
   * Get options for a specific field
   */
  getOptions(reportType, field, reportOptions, dynamicBinderOptions, findReportByType) {
    const options = reportOptions.find((option) => option.ReportType === reportType);

    if (!options) {
      return [];
    }

    if (field.dropdownOptionsType === 'CoverholderList') {
      return options.CoverholderOptions || [];
    } else if (field.dropdownOptionsType === 'BindersList') {
      // For BindersList, we need to get the selected coverholder to fetch dynamic options
      const report = findReportByType(reportType);
      
      if (report) {
        const coverholderField = report.fields.find(f => f.dropdownOptionsType === 'CoverholderList');
        
        if (coverholderField && coverholderField.selectedOption) {
          const key = `${reportType}_${coverholderField.selectedOption.id}`;
          return dynamicBinderOptions[key] || [];
        }
      }
      // Fallback to static options if no coverholder is selected
      return options.BinderOptions || [];
    } else if (!field.dropdownOptionsType) {
      // Handle period selection and other fields without dropdownOptionsType
      return options.ReportOptions || [];
    }
    
    return [];
  }

  /**
   * Prepare download parameters from report fields
   */
  prepareDownloadParams(report) {
    let binder = null;
    let selectedOption = null;
    let coverholder = null;

    if (report.fields) {
      report.fields.forEach(field => {
        if (field.type === 'select') {
          if (field.dropdownOptionsType === 'BindersList') {
            binder = field.selectedOption.id;
          } else if (field.dropdownOptionsType === 'CoverholderList') {
            coverholder = field.selectedOption.id; // Company_PK from selected Coverholder
          } else if (!field.dropdownOptionsType) {
            // This is likely the period selection field
            selectedOption = field.selectedOption.id;
          }
        }
      });
    }

    return {
      reportType: report.reportType,
      coverholder: coverholder,
      binder: binder,
      selectedOption: selectedOption,
      language: 'en',
    };
  }
}

export default ReportService;
